//================================================================
//  Component: Enhancement Form
//================================================================

//  Purpose: This contains the form for an enhancement

//  Properties:
//    - collectionid = {string, Firestore collection id}
//    - documentid = {string, Firestore document id}
//    - task = {object, Firestore document}

//  Example:
//    <EnhancementForm
//      collectionid={collectionid}
//      documentid={documentid}
//      task={task}
//    ></EnhancementForm>    

// ==============================================================

// Libraries
import React from 'react';

// Contexts

// Components
import LiveField from '../../../../../Components/LiveField/LiveField';
import DynamicVendorRating from '../../../../../Components/DynamicVendorRating/DynamicVendorRating';
import DynamicAddLinkLive from '../../../../../Components/DynamicAddLinkLive/DynamicAddLinkLive';
import FileUploadLiveField from '../../../../../Components/FileUploadLiveField/FileUploadLiveField';
import DigitalSMEInitiativeLive from '../../../../../Components/DigitalSMEInitiativeLive/DigitalSMEInitiativeLive';

// CSS

export default function EnhancementForm({
    collectionid,
    documentid,
    task
}) {

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div>

            {/* ------------- Enhancement------------- */}

            <h5 className='text-[18px] text-teal-600 mb-2'> Enhancements</h5>

            {/*  1. Vendor Risk Rating (UpGuard)*/}
            <LiveField
                type={'lookup'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.vendor'}
                label={<label className='font-medium'> 1. Vendor Risk Rating (UpGuard)</label>}
                defaultValue={task?.demandassessment?.vendor?.vendorname}
                placeholder={'Search vendor by name'}
                dataFieldCollectionid={'vendors'}
                dataFieldPrimary={'vendorname'}
                dataFieldSecondary={'tradingas'}
                dataFieldTertiary={'overallrating'}
                dataFieldTertiaryrequired={false}
                dataFieldQuaternary={'lastcyberriskupdated'}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled') || task?.executionnotrequired === true ? true : false}
            ></LiveField>

            {
                task?.demandassessment?.vendor?.vendorname &&
                <DynamicVendorRating 
                    letter={task?.demandassessment?.vendor?.overallrating?.grade} 
                    score={task?.demandassessment?.vendor?.overallrating?.score}
                    lastmodifieddate={task?.demandassessment?.vendor?.lastcyberriskupdated}
                ></DynamicVendorRating>
            }


            {/* 2. Does this initiative relate to any of the following? */}
            <DigitalSMEInitiativeLive
                initiativeCategories={task?.demandassessment?.initiativecategories||[]}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.initiativecategories'}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></DigitalSMEInitiativeLive>

            {/* 3. Does this request align to the Business / Functions / Digital technology strategy? */}
            <LiveField
                type={'choice'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.businessfunctiontechalignment'}
                defaultValue={task?.demandassessment?.businessfunctiontechalignment}
                label={<label className='font-medium'> 3. Does this request align to the Business / Functions / Digital technology strategy? </label>}
                options={[
                    'Yes',
                    'No',
                    'Unknown',
                    'N/A',
                ]}
                styleInput={{ width: '350px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>


            {/* 4. Please provide any relevant commentary regarding strategy alignment */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.strategyalignmentcommentary'}
                label={<label className='font-medium'> 4. Please provide any relevant commentary regarding strategy alignment</label>}
                defaultValue={task?.demandassessment?.strategyalignmentcommentary}
                placeholder={'Summarise strategy alignment'}
                styleInput={{ maxWidth: '625px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  5. Has an options analysis been undertaken? What were the outcomes? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.optionsanalysis'}
                label={<label className='font-medium'> 5. Has an options analysis been undertaken? What were the outcomes?</label>}
                defaultValue={task?.demandassessment?.optionsanalysis}
                placeholder={'Leave blank if unknown or not applicable'}
                styleInput={{ maxWidth: '625px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/* 6. What is the Digital recommendation for this request? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.digitalrecommendation'}
                label={<label className='font-medium'>6. What is the Digital recommendation for this request?</label>}
                defaultValue={task?.demandassessment?.digitalrecommendation}
                placeholder={'Based on engagement to date, does Digital endorse the scope of this request?'}
                styleInput={{ maxWidth: '625px' }}
                disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
            ></LiveField>

            {/*  7. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.*/}
            <div className='mt-3'>
                <label className='font-medium'> 7. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.</label>

                {/* Attach Files */}
                <FileUploadLiveField
                    label={<label className='font-medium'> Attach documents </label>}
                    defaultValue={task?.demandassessment?.attacheddocuments}
                    collectionid={collectionid}
                    documentid={documentid}
                    fieldpath={'demandassessment.attacheddocuments'}
                    disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                ></FileUploadLiveField>

                {/* Add links */}
                <div>
                    <label className='font-medium mb-2'> Add links </label>
                    <DynamicAddLinkLive
                        links={task?.demandassessment?.links}
                        collectionid={collectionid}
                        documentid={documentid}
                        fieldpath={'demandassessment.links'}
                        disabled={(task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ) ? true : false}
                    ></DynamicAddLinkLive>
                </div>
            </div>
        </div>
    );

}
